import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { MutationOptionsAlone } from 'apollo-angular/types';
import { UpdateJobGQL, UpdateJobMutation, UpdateJobMutationVariables } from 'graphql.generated';
import { map, tap, catchError, of, exhaustMap, mergeMap } from 'rxjs';

import { generateMutationContext } from '../../../core/auth/session';
import { selectJobFranchiseId, selectJobId } from '../../job-tool.reducer';

import { JobStatusDropdownActions } from './job-status-dropdown.actions';

export const moveToNextStage = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(JobStatusDropdownActions.moveToNextStageMenuItemClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJobId),
          store.select(selectJobFranchiseId),
        ]),
        map(([ { stage }, jobId, jobFranchiseId ]): {
          vars: UpdateJobMutationVariables;
          opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>;
        } => {
          return {
            vars: {
              updateJobs: [{
                jobId,
                stage,
                promoteReason: 'Manually Promoted',
              }]
            },
            opts: { context: generateMutationContext({
              zone: jobFranchiseId,
            }) },
          };
        }),
        exhaustMap(({ vars, opts }) => updateJobGQL.mutate(vars, opts).pipe(
          tap((result) => {
            const { isSuccess } = result.data?.updateJobs;
            if (!isSuccess) {
              throw new Error('Error updating job stage');
            }
          }),
          map((result) => {
            const { jobs: [updatedJob] } = result.data?.updateJobs;
            return JobStatusDropdownActions.moveToNextStageSuccess({ updatedJob });
          }),
        )),
        catchError((error) => of(JobStatusDropdownActions.moveToNextStageError({
          error,
          customErrorMessage: `Error setting job stage to ${action.stage}`
        }))),
      )),
    );
  },
  { functional: true, dispatch: true },
);
